import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { apiCreate, apiDelete, apiList, apiUpdate } from '@/lib/api';
import { normalizeLocalDate } from '@/lib/date-utils';

export interface ContaPagar {
  id: string;
  user_id: string;
  fornecedor_id?: string;
  descricao: string;
  categoria: string;
  valor: number;
  data_vencimento: string;
  data_pagamento?: string;
  status: 'Pendente' | 'Paga' | 'Vencida' | 'Cancelada';
  forma_pagamento?: string;
  observacoes?: string;
  created_at: string;
  updated_at: string;
  fornecedores?: {
    nome: string;
  };
}

export interface CreateContaPagarData {
  fornecedor_id?: string;
  descricao: string;
  categoria: string;
  valor: number;
  data_vencimento: string;
  data_pagamento?: string;
  status: 'Pendente' | 'Paga' | 'Vencida' | 'Cancelada';
  forma_pagamento?: string;
  observacoes?: string;
}

const normalizeContaPagar = <T extends Partial<ContaPagar>>(data: T): T => ({
  ...data,
  ...(data.data_vencimento ? { data_vencimento: normalizeLocalDate(data.data_vencimento) } : {}),
  ...("data_pagamento" in data ? { data_pagamento: normalizeLocalDate(data.data_pagamento) } : {}),
});

export function useContasPagar() {
  const [contas, setContas] = useState<ContaPagar[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const fetchContas = async () => {
    if (!user) {
      setContas([]);
      return;
    }
    
    try {
      setLoading(true);
      
      const data = await apiList<ContaPagar>('contas_pagar');
      
      const processedData = (data || []).map(c => ({
        ...normalizeContaPagar(c),
        status: c.status as 'Pendente' | 'Paga' | 'Vencida' | 'Cancelada'
      }));
      
      setContas(processedData);
    } catch (error: any) {
      console.error('fetchContas error:', error);
      toast.error('Erro ao carregar contas: ' + error.message);
      setContas([]);
    } finally {
      setLoading(false);
    }
  };

  const createConta = async (contaData: CreateContaPagarData) => {
    if (!user) {
      toast.error('Usuário não autenticado');
      return;
    }

    try {
      const data = await apiCreate<ContaPagar>('contas_pagar', {
        ...normalizeContaPagar(contaData),
        user_id: user.id
      });

      const processedConta = {
        ...data,
        status: data.status as 'Pendente' | 'Paga' | 'Vencida' | 'Cancelada'
      };

      setContas(prev => [...prev, processedConta]);
      toast.success('Conta a pagar criada com sucesso!');
      return processedConta;
    } catch (error: any) {
      console.error('createConta error:', error);
      toast.error('Erro ao criar conta: ' + error.message);
      throw error;
    }
  };

  const updateConta = async (id: string, contaData: Partial<CreateContaPagarData>) => {
    try {
      const data = await apiUpdate<ContaPagar>('contas_pagar', id, normalizeContaPagar(contaData));

      const processedConta = {
        ...data,
        status: data.status as 'Pendente' | 'Paga' | 'Vencida' | 'Cancelada'
      };

      setContas(prev => prev.map(c => c.id === id ? processedConta : c));
      toast.success('Conta atualizada com sucesso!');
      return processedConta;
    } catch (error: any) {
      console.error('updateConta error:', error);
      toast.error('Erro ao atualizar conta: ' + error.message);
      throw error;
    }
  };

  const deleteConta = async (id: string) => {
    try {
      await apiDelete('contas_pagar', id);

      setContas(prev => prev.filter(c => c.id !== id));
      toast.success('Conta excluída com sucesso!');
    } catch (error: any) {
      console.error('deleteConta error:', error);
      toast.error('Erro ao excluir conta: ' + error.message);
      throw error;
    }
  };

  const pagarConta = async (id: string, dataPagamento: string, formaPagamento: string) => {
    try {
      const data = await apiUpdate<ContaPagar>('contas_pagar', id, {
          data_pagamento: normalizeLocalDate(dataPagamento),
          forma_pagamento: formaPagamento,
          status: 'Paga'
        });

      const processedConta = {
        ...data,
        status: data.status as 'Pendente' | 'Paga' | 'Vencida' | 'Cancelada'
      };

      setContas(prev => prev.map(c => c.id === id ? processedConta : c));
      toast.success('Conta marcada como paga!');
      return processedConta;
    } catch (error: any) {
      console.error('pagarConta error:', error);
      toast.error('Erro ao pagar conta: ' + error.message);
      throw error;
    }
  };

  useEffect(() => {
    if (user) {
      fetchContas();
    } else {
      setContas([]);
      setLoading(false);
    }
  }, [user?.id]);

  return {
    contas,
    loading,
    createConta,
    updateConta,
    deleteConta,
    pagarConta,
    refetch: fetchContas
  };
}
